import React, { useContext, useEffect, useRef, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import CardHeader from "./CardHeader";
import { cardProps } from "./Card";
import CardCoverageFacility, { coverageFacility } from "./CardCoverageFacility";
import Underwriter from "./Underwriter";
import States from "./States";
import Files, { fileType } from "./Files";
import { getCarrierData, getFilterData, getLogsApi } from "../api/getApiData";
import { formatCarrierData, formatStatesData } from "../functions/formatData";
import { FilterContext } from "../context/filterContext";
import CmsIcon from "../../../assests/img/cms-icon.svg?react";
import AddIcon from "../../../assests/img/add.svg?react";
import CloseIcon from "../../../assests/img/close.svg?react";
import TextInput from "./TextInput";
import classNames from "classnames";
import Button from "./Button";
import { updateField } from "../../Content";
import { checkNull, getUserPermisson, wholesalerBg } from "../functions/utils";
import { deleteCompanyDetail, deleteDataApi } from "../api/deleteApi";
import {
  addCompanyCategory,
  addCompanyState,
  addContact,
  addFacilityType,
  addLog,
  addCompanyDetail,
} from "../api/postApi";
import Edit from "./Edit";
import { handleFilter, isFilterSelected } from "../functions/filter";
import { toast } from "react-toastify";
import Popup from "./Popup";
import { UserContext } from "../context/userContext";
import { AuthContext } from "../context/authContext";

export interface cardDetailViewProps extends cardProps {
  onClose: () => void;
  isUnderwriterSelected: (isSelected: boolean) => void;
  addNew: boolean;
  favourite: boolean;
  onWholesalersSelect: (selected: boolean) => void;
  updated: (input: string) => void;
}

export default function CardDetailView(props: cardDetailViewProps) {
  const {
    data: { id, uniqueRequirements, companyId, groupId },
    onWholesalersSelect,
    updated,
  } = props;

  const { filterData } = useContext(FilterContext);
  const {
    data: { userId, permissions },
  } = useContext(UserContext);
  const { accounts } = useContext(AuthContext);

  const getSubmissionRequirementsString = () => {
    if (props.data.submissionRequirements !== null) {
      return props.data.submissionRequirements.split(",");
    } else {
      return [];
    }
  };

  const [underwriterData, setUnderwriterData] = useState<any[]>([]);
  const [statesData, setStatesData] = useState([]);
  const [files, setFiles] = useState<fileType[]>([]);
  const [notes, setNotes] = useState(props.data.notes);
  const [name, setName] = useState(props.data.name);
  const [coverage, setCoverage] = useState(props.data.coverage);
  const [distribution, setDistribution] = useState<string>(
    props.data.distribution
  );
  const [minimumCms, setMinimumCms] = useState(props.data.minimumCms);
  const [minimumTiv, setMinimumTiv] = useState(props.data.minimumTiv);
  const [type, setType] = useState(props.data.type);
  const [facilityTypes, setFacilityTypes] = useState(props.data.facilityTypes);
  const [uniqueReq, setUniqueReq] = useState(uniqueRequirements);
  const [edit, setEdit] = useState(false);
  const [addUnderwriter, setAddUnderwriter] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [contactsList, setContactList] = useState(() =>
    filterData.contacts
      .filter((data: any) => data.name && data.name.trim() !== "")
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
  );
  const [removeUnderwriter, setRemoveUnderwriter] = useState(0);
  const [submissionRequirements, setSubmissionRequirements] = useState(
    getSubmissionRequirementsString()
  );
  const [categories, setCategories] = useState(props.data.categories);
  const [showDelete, setShowDelete] = useState(false);
  const [logo, setLogo] = useState(props.data.logo);
  const [loadingUnderwriter, setLoadingUnderwriter] = useState(true);
  const [loadingStates, setLoadingStates] = useState(true);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [submissionreqId, setSubmissionReqId] = useState(0);
  const [updatedValues, setUpdatedValues] = useState({
    minimumTiv: props.data.minimumTiv,
    minimumCms: props.data.minimumCms,
    uniqueRequirements: props.data.uniqueRequirements,
    notes: props.data.notes,
  });
  const [unsavedPopup, setUnsavedPopup] = useState("");
  const [lastUpdatedOn, setLastUpdatedOn] = useState("N/A");

  const dropdownRef = useRef<HTMLDivElement>(null);
  let log = "";

  const newUnderwriterInitialData = {
    id: "0",
    name: "",
    title: "",
    phone: "",
    mobile: "",
    department: "",
    email: "",
    submissionEmail: "",
    address: "",
    coverageId: "0",
  };
  const [selectedUnderwriter, setSelectedUnderwriter] = useState<number | null>(
    null
  );
  const [newUnderwriter, setNewUnderwriter] = useState<{
    id: string;
    name: string;
    title: string;
    phone: string;
    mobile: string;
    department: string;
    email: string;
    submissionEmail: string;
    address: string;
    coverageId: string;
  }>(newUnderwriterInitialData);

  const addLogs = (log: string) => {
    addLog(
      { logText: log, createdById: userId, CompanyGroupId: groupId },
      accounts
    );
  };

  const getLastUpdatedOn = async () => {
    const { res } = await getLogsApi(
      `Logs?$filter=CompanyGroupId eq ${groupId}&$orderby=createdDateTime desc`,
      accounts
    );
    if (res.value.length) {
      setLastUpdatedOn(new Date(res.value[0].createdDateTime).toString());
    }
  };

  const getCompanyStateData = async () => {
    setLoadingStates(true);
    const data = await getFilterData(
      `CompanyState?$filter=CompanyDetailId eq ${id}
            &$orderby=StateId asc`,
      accounts
    );
    const formattedData = formatStatesData(data, filterData.states);
    setStatesData(formattedData);
    setLoadingStates(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setShowContacts(false);
    }
  };

  const getIndexOfCurrentUnderwriter = (underwriterData: any[]) => {
    const index = underwriterData.findIndex(
      (data: any) => data.coverage.value === props.data.coverage.value
    );
    return index;
  };

  const getFiles = async () => {
    setLoadingFiles(true);
    const data = await getFilterData(
      `Files?$filter=CompanyGroupId eq ${groupId}`,
      accounts
    );
    setFiles(data);
    setLoadingFiles(false);
  };

  useEffect(() => {
    async function getUnderwriterData() {
      setLoadingUnderwriter(true);
      const {
        carrier,
        contacts,
        facilityType,
        coverage,
        categories,
        companyCategories,
      } = filterData;
      const data = await getCarrierData(
        `CompanyDetail?$filter=CompanyId eq ${companyId}`,
        accounts
      );
      const formattedData = formatCarrierData(
        data,
        carrier,
        contacts,
        facilityType,
        coverage,
        categories,
        companyCategories
      );
      const index = getIndexOfCurrentUnderwriter(formattedData);
      if (~index) {
        [formattedData[0], formattedData[index]] = [
          formattedData[index],
          formattedData[0],
        ];
      }
      // 👇 Flatten contact arrays into individual underwriter entries
      const flattenedData = formattedData.flatMap((entry: any) =>
        entry.contact.map((c: any) => ({
          ...entry, // include all fields from the original entry
          contact: c,
        }))
      );

      setUnderwriterData(flattenedData);
      setLoadingUnderwriter(false);
    }
    getUnderwriterData();
    getCompanyStateData();
    getFiles();
    getLastUpdatedOn();
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [companyId, filterData, props.data.coverage.value]);

  const handleUpdateNotes = async () => {
    const reqBody = {
      fieldId: groupId,
      fieldName: "Notes",
      fieldValue: notes,
    };
    const status = await props.handleUpdate(reqBody, updateField.notes);
    if (status === 200) {
      setNotes(reqBody.fieldValue);
      setUpdatedValues({ ...updatedValues, notes: reqBody.fieldValue });
    } else {
      setNotes(props.data.notes);
    }
  };

  const validateUnderwriterDetails = () => {
    let valid = false;
    if (newUnderwriter.name.trim().length === 0) {
      toast.error("Please add or enter underwriter name");
    } else {
      valid = true;
    }
    return valid;
  };

  const handleUpdateUnderwriter = async () => {

    const coverageUnderwritersMap = underwriterData.reduce((map, u) => {
      const ids = Array.isArray(u.ContactId) ? u.ContactId.map(Number) : [];
      if (!map[u.CoverageId]) {
        map[u.CoverageId] = []; 
      }
      map[u.CoverageId].push(...ids);
      return map;
    }, {} as Record<number, number[]>);

    if (!validateUnderwriterDetails()) {
      return;
    }

    const reqBody = {
      name: newUnderwriter.name,
      phone: newUnderwriter.phone,
      mobile: newUnderwriter.mobile,
      email: newUnderwriter.email,
      address: newUnderwriter.address,
      title: newUnderwriter.title,
      submissionEmail: newUnderwriter.submissionEmail,
      department: newUnderwriter.department,
    };
    
    if (addUnderwriter) {
      const res = await toast.promise(addContact(reqBody, accounts), {
        pending: "Adding underwriter",
      });

      if (res.status === 201) {
        const newId = res.data?.value?.[0]?.id;
        if (newId) {
          newUnderwriter.id = newId;
        }

        toast.success("Added underwriter successfully");
        setAddUnderwriter(false);
        log = `Added Underwriter ${newUnderwriter.name} with Email: ${newUnderwriter.email}`;
      } else if (res.status === 409) {
        if ((coverageUnderwritersMap[coverage.id]?.
          includes(Number(newUnderwriter.id)) ?? false) ) {
          toast.error("Failed to add underwriter, Duplicate entry");
        } else {
          setAddUnderwriter(false);
        }
      } else {
        toast.error("Failed to add underwriter");
      }
    }
    if ((coverageUnderwritersMap[coverage.id]?.
          includes(Number(newUnderwriter.id)) ?? false) 
      || (selectedUnderwriter !== null && 
    selectedUnderwriter !== Number(newUnderwriter.id))) {
      const status = await toast.promise(
        props.handleUpdate(
          reqBody,
          updateField.underwriter,
          `Contact/id/${selectedUnderwriter}`
        ),
        { pending: "Updating underwriter" }
      );
      const statusCode = status === 200 ? 200 : (status as any).status ?? 0;
      if (statusCode === 200) {
        const temp: any = [...underwriterData];
        if (selectedUnderwriter) {
          const index = temp.findIndex(
            (data: any) => data.contact.id === selectedUnderwriter
          );
          temp[index] = {
            ...temp[index],
            contact: { ...temp[index].contact, ...reqBody },
          };
        }
        setUnderwriterData(temp);
        setSelectedUnderwriter(null);
        toast.success("Underwriter updated");
        log = `Updated Underwriter ${reqBody.name} with Email - ${reqBody.email}`;
        updated("u");
      } else if (statusCode === 409) {
        toast.error("Failed to update underwriter, Duplicate entry");
      } else {
        toast.error("Failed to update underwriter");
      }
    } else if (selectedUnderwriter === null) {
      const createPromises = facilityTypes.map(async (ft) => {
        const createReqBody = {
          CompanyId: companyId,
          CoverageId: coverage.id,
          FacilityTypeId: ft.id,
          ContactId: newUnderwriter.id,
          Notes: notes,
          SubmissionRequirement: submissionRequirements.join(","),
          MinimumCMS: minimumCms,
          MinimumTIV: minimumTiv,
          CompanyGroupId: `${companyId}${coverage.id}`,
          UniqueRequirements: uniqueReq,
          CreatedById: userId,
        };

        // eslint-disable-next-line max-len
        const res = await toast.promise(
          addCompanyDetail(createReqBody, accounts),
          {
            pending: `Adding underwriter for ${ft.value}`,
          }
        );

        if (res.status === 201) {
          toast.success(`Underwriter added for ${ft.value}`);
          log = `Added new underwriter ${newUnderwriter.name} for ${coverage.value} - ${ft.value}`;
        }
      });

      await Promise.all(createPromises);

      if (!(coverageUnderwritersMap[coverage.id]?.
          includes(Number(newUnderwriter.id)) ?? false) ) {
        const newUnderwriterEntry = {
          contact: {
            ...newUnderwriter,
            id: newUnderwriter.id,
          },
          coverage: coverage,
          facilityTypes: facilityTypes,
        };

        setUnderwriterData((prev: any) => [...prev, newUnderwriterEntry]);
      }
      updated("u");
    }

    addLogs(log);
  };

  const handleOnSelectUnderwriter = (id: number | null) => {
    setSelectedUnderwriter(id);
    if (id !== null) {
      props.isUnderwriterSelected(true);
      const index = underwriterData.findIndex(
        (data: any) => data.contact.id === id
      );
      const data: any = underwriterData[index];
      setNewUnderwriter({
        id: id.toString(),
        name: checkNull(data.contact.name, "string"),
        title: checkNull(data.contact.title, "string"),
        phone: checkNull(data.contact.phone, "string"),
        mobile: checkNull(data.contact.mobile, "string"),
        department: checkNull(data.contact.department, "string"),
        email: checkNull(data.contact.email, "string"),
        submissionEmail: checkNull(data.contact.submissionEmail, "string"),
        address: checkNull(data.contact.address, "string"),
        coverageId: checkNull(data.coverage.id, "string"),
      });
    } else {
      props.isUnderwriterSelected(false);
    }
  };

  const handleUnderwriterData = (field: string, value: string) => {
    setNewUnderwriter({ ...newUnderwriter, [field]: value });
  };

  const handleStateChange = async (
    id: number,
    action: "add" | "remove",
    data?: any
  ) => {
    let stateId = 0;
    if (data) {
      stateId = data.StateId;
    }
    const stateName = filterData.states.filter((state) => state.id === stateId);
    if (action === "remove") {
      const res = await deleteDataApi(
        `CompanyState/CompanyStateId/${id}`,
        accounts
      );
      if (res === 204) {
        await getCompanyStateData();
        log = `Removed State - ${stateName[0].value} for ${name} with Coverage - ${coverage.value}`;
        addLogs(log);
        return true;
      }
    } else if (action === "add" && data) {
      const res = await addCompanyState(`CompanyState`, data, accounts);
      if (res.status === 201) {
        await getCompanyStateData();
        log = `Added State Id - ${stateName[0].value} for ${name} with Coverage - ${coverage.value}`;
        addLogs(log);
        return true;
      }
    }
    return false;
  };

  const handleHeaderUpdate = async (reqBody: any, field: updateField) => {
    if (field === updateField.category) {
      if (reqBody.categoryGroupId) {
        const res = await deleteDataApi(
          `CompanyCategory/companyCategoryId/${reqBody.categoryGroupId}`,
          accounts
        );
        if (res === 204) {
          updated("c");
          const temp = categories.filter(
            (data) => data.groupId !== reqBody.categoryGroupId
          );
          setCategories(temp);
          const catgeoryName = categories.filter(
            (data) => data.groupId === reqBody.categoryGroupId
          );
          if (catgeoryName.length) {
            log = `Removed Category - ${catgeoryName[0].value} for ${name} with Coverage - ${coverage.value}`;
          } else {
            log = `Removed Category for ${name} with Coverage - ${coverage.value}`;
          }
        }
      } else {
        const data = {
          companyGroupId: groupId,
          categoryId: reqBody.categoryId,
        };
        const res = await addCompanyCategory(data, accounts);
        if (res.status === 201) {
          updated("c");
          const temp = [...categories];
          const selected = filterData.categories.filter(
            (data) => data.id === reqBody.categoryId
          );
          if (selected.length) {
            temp.push({
              ...selected[0],
              groupId: res.data.value[0].companyCategoryId,
            });
            setCategories(temp);
            const companyCategories: any = [...filterData.companyCategories];
            companyCategories.push(res.data.value[0]);
            log = `Added Category - ${selected[0].value} for ${name} with Coverage - ${coverage.value}`;
          }
        }
      }
      addLogs(log);
      return;
    }
    const res = await props.handleUpdate(
      reqBody,
      field,
      `Company/id/${companyId}`
    );
    if (res === 200) {
      if (field === updateField.name) {
        setName(reqBody.value);
        log = `Updated Name from ${name} to ${reqBody.value} for ${name} with Coverage - ${coverage.value}`;
      }
      if (field === updateField.distribution) {
        setDistribution(reqBody.Distribution);
        log = `Updated Distribution from ${distribution} to ${reqBody.Distribution} for ${name} with Coverage - ${coverage.value}`;
      }
      if (field === updateField.type) {
        setType(reqBody.CompanyType);
        log = `Updated Company type from ${type} to ${reqBody.CompanyType} for ${name} with Coverage - ${coverage.value}`;
      }
      if (field === updateField.logo) {
        setLogo(reqBody.Logo);
        log = `Logo updated for ${name}`;
      }
      addLogs(log);
      updated("h");
    }
  };

  const handleUpdateMinimumTiv = async () => {
    const reqBody = {
      fieldId: groupId,
      fieldName: "MinimumTiv",
      fieldValue: minimumTiv,
    };
    const res = await props.handleUpdate(reqBody, updateField.minimumTiv);
    if (res === 200) {
      setMinimumTiv(reqBody.fieldValue);
      setUpdatedValues({ ...updatedValues, minimumTiv: reqBody.fieldValue });
      log = `Updated Miniumum TIV from ${minimumTiv} to ${reqBody.fieldValue} for ${name} with Coverage - ${coverage.value}`;
      addLogs(log);
    } else {
      setMinimumTiv(props.data.minimumTiv);
    }
    updated("h");
  };

  const handleUpdateMinimumCms = async () => {
    const reqBody = {
      fieldId: groupId,
      fieldName: "MinimumCms",
      fieldValue: minimumCms,
    };
    const res = await props.handleUpdate(reqBody, updateField.mnimumCms);
    if (res === 200) {
      setMinimumCms(reqBody.fieldValue);
      setUpdatedValues({ ...updatedValues, minimumCms: reqBody.fieldValue });
      log = `Updated Miniumum CMS from ${minimumCms} to ${reqBody.fieldValue} for ${name} with Coverage - ${coverage.value}`;
      addLogs(log);
    } else {
      setMinimumCms(props.data.minimumCms);
    }
    updated("h");
  };

  const handleEdit = () => {
    if (edit) {
      setEdit(false);
    } else {
      setEdit(true);
    }
  };

  const handleUpdateCoverage = async (
    id: number,
    value: string,
    field: coverageFacility,
    logo: string
  ) => {
    if (field === coverageFacility.coverage) {
      const reqBody = {
        fieldId: groupId,
        fieldName: "Coverage",
        fieldValue: id,
      };
      const res = await props.handleUpdate(reqBody, updateField.coverage);
      if (res === 200) {
        setCoverage({ id, value, logo });
        log = `Updated Coverage from ${coverage.value} to ${value} for ${name} with Coverage - ${coverage.value}`;
      }
    }
    if (field === coverageFacility.facility) {
      const isSelected = isFilterSelected(id, facilityTypes);
      if (isSelected) {
        const reqBody = {
          CompanyId: companyId,
          CoverageId: coverage.id,
          FacilityTypeId: id,
          DeleteType: "facility",
        };
        const res = await deleteCompanyDetail(reqBody, accounts);
        if (res.status === 201) {
          const filter = handleFilter(
            id,
            value,
            filterData.facilityType,
            facilityTypes
          );
          setFacilityTypes(filter);
          log = `Removed Facility ${value} for ${name} with Coverage - ${coverage.value}`;
        }
      } else {
        const reqBody = {
          CompanyId: companyId,
          ContactId: props.data.contact[0]?.id,
          CoverageId: coverage.id,
          FacilityTypeId: id,
          Notes: notes,
          SubmissionRequirement: submissionRequirements,
          MinimumCMS: minimumCms,
          MinimumTIV: minimumTiv,
          CompanyGroupId: groupId,
          CreatedById: userId,
        };
        const res = await addFacilityType(reqBody, accounts);
        if (res.status === 201) {
          const reqBody = {
            StateId: filterData.states[1].id,
            CompanyDetailId: res.data.value[0].CompanyDetailId,
            CreatedById: userId,
          };
          const data = await addCompanyState(`CompanyState`, reqBody, accounts);
          if (data.status === 201) {
            toast.success("Facility type added succesfully");
            const filter = handleFilter(
              id,
              value,
              filterData.facilityType,
              facilityTypes
            );
            setFacilityTypes(filter);
            log = `Add Facility ${value} for ${name} with Coverage - ${coverage.value}`;
          }
        }
      }
    }
    addLogs(log);
    updated("h");
  };

  const handleUniqueReqUpdate = async () => {
    const reqBody = {
      fieldId: groupId,
      fieldName: "UniqueRequirements",
      fieldValue: uniqueReq,
    };
    const res = await props.handleUpdate(reqBody, updateField.uniqueReq);
    if (res === 200) {
      setUniqueReq(reqBody.fieldValue);
      setUpdatedValues({
        ...updatedValues,
        uniqueRequirements: reqBody.fieldValue,
      });
      log = `Updated Unique requirements from ${uniqueRequirements} to ${reqBody.fieldValue}  for ${name} with Coverage - ${coverage.value}`;
    } else {
      setUniqueReq(props.data.uniqueRequirements);
    }
    addLogs(log);
  };

  const closeunderwriterDialog = () => {
    if (addUnderwriter) {
      setAddUnderwriter(false);
    } else {
      setSelectedUnderwriter(null);
    }
  };

  const filterContacts = (text: string) => {
    setNewUnderwriter({ ...newUnderwriter, name: text });

    if (text.length) {
      const temp: any = filterData.contacts
        .filter(
          (data: any) =>
            data.name &&
            data.name.trim() !== "" &&
            data.name.toLowerCase().includes(text.toLowerCase())
        )
        .sort((a: any, b: any) => a.name.localeCompare(b.name));
      setContactList(temp);
      setShowContacts(temp.length > 0);
    } else {
      setContactList([]);
      setShowContacts(false);
    }
  };

  const selectedContact = (id: number) => {
    const contact: any = filterData.contacts.filter(
      (data: any) => data.id === id
    );
    if (contact.length) {
      const temp = {
        id: id.toString(),
        name: checkNull(contact[0].name, "string"),
        title: checkNull(contact[0].title, "string"),
        department: checkNull(contact[0].department, "string"),
        email: checkNull(contact[0].email, "string"),
        phone: checkNull(contact[0].phone, "string"),
        mobile: checkNull(contact[0].mobile, "string"),
        address: checkNull(contact[0].address, "string"),
        submissionEmail: checkNull(contact[0].submissionEmail, "string"),
        coverageId: checkNull(contact[0].coverage, "string"),
      };
      setNewUnderwriter(temp);
      setShowContacts(false);
    }
  };

  const style = {
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: "inter",
    fontWeight: "500",
  };

  const underwriterFields = [
    {
      id: "name",
      title: "Name",
      placeholder: "Enter Name Here",
      width: "100%",
    },
    {
      id: "title",
      title: "Title",
      placeholder: "Enter Title Here",
      width: "100%",
    },
    {
      id: "phone",
      title: "Telephone",
      placeholder: "Enter Telephone Here",
      width: "100%",
    },
    {
      id: "mobile",
      title: "Mobile",
      placeholder: "Enter Mobile Here",
      width: "100%",
    },
    {
      id: "department",
      title: "Department",
      placeholder: "Enter Department Here",
      width: "200%",
    },
    {
      id: "email",
      title: "Email",
      placeholder: "Enter Email Here",
      width: "100%",
    },
    {
      id: "submissionEmail",
      title: "Submission Email",
      placeholder: "Enter Submission Email Here",
      width: "100%",
    },
    {
      id: "address",
      title: "Address",
      placeholder: "Enter Address Here",
      width: "200%",
    },
  ];

  const handleAddNewUnderwriter = () => {
    setNewUnderwriter(newUnderwriterInitialData);
    setAddUnderwriter(true);
  };

  const removeUnderwriterApi = async () => {
    const reqBody = {
      CompanyId: companyId,
      CoverageId: coverage.id,
      ContactId: removeUnderwriter,
      DeleteType: "underwriter",
    };

    const res = await deleteCompanyDetail(reqBody, accounts);
    if (res.status) {
      toast.success("Deleted underwriter successfully");
      props.handleUpdate({ data: "h" }, updateField.refreshData);
      log = `Deleted Underwriter ${name} with Coverage - ${coverage.value}`;
      addLogs(log);
    }

    // const res = await toast.promise(
    //   deleteDataApi(`Contact/id/${removeUnderwriter}`, accounts),
    //   { pending: "Removing underwriter" }
    // );
    // if (res.status === 200) {
    //   toast.success("Underwriter removed sucesffully");
    //   log = `Removed underwriter with id ${removeUnderwriter}`;
    // } else if (res.status === 400) {
    //   toast.error(
    //     "Underwriter remove failed, because the selected underwriter has been   already assigned"
    //   );
    // }
    addLogs(log);
  };

  const handleRemoveUnderwriter = () => {
    if (selectedUnderwriter !== null) {
      setRemoveUnderwriter(selectedUnderwriter);
    }
  };

  const handleSubmissionRequirements = async (id: number) => {
    setSubmissionReqId(id);
    const isSelected = submissionRequirements.includes(id.toString());
    const temp = [...submissionRequirements];
    if (isSelected) {
      const index = submissionRequirements.findIndex(
        (data) => data === id.toString()
      );
      if (~index) {
        temp.splice(index, 1);
      }
    } else {
      temp.push(id.toString());
    }
    const reqBody = {
      fieldId: groupId,
      fieldName: "SubmissionRequirements",
      fieldValue: temp.join(","),
    };
    const res = await props.handleUpdate(reqBody, updateField.submissionReq);
    if (res === 200) {
      setSubmissionRequirements(temp);
      log = `Updated Submission requirement for ${name} with Coverage - ${coverage.value}`;
      addLogs(log);
      setSubmissionReqId(0);
    } else {
      toast.error("Submission requirements update failed");
      setSubmissionRequirements(props.data.submissionRequirements.split(","));
      setSubmissionReqId(0);
    }
  };

  const handleDelete = async () => {
    const reqBody = {
      CompanyId: companyId,
      CoverageId: coverage.id,
      FacilityTypeId: 0,
      DeleteType: "card",
    };
    const res = await deleteCompanyDetail(reqBody, accounts);
    if (res.status) {
      toast.success("Deleted card details successfully");
      props.handleUpdate({ data: "h" }, updateField.refreshData);
      log = `Deleted Card ${name} with Coverage - ${coverage.value}`;
      addLogs(log);
    }
  };

  const handleOnClose = () => {
    let unsavedChanges = "";
    if (minimumTiv !== updatedValues.minimumTiv) {
      unsavedChanges = "Min TIV has been changed but not updated";
    } else if (minimumCms !== updatedValues.minimumCms) {
      unsavedChanges = "Min CMS has been changed but not updated";
    } else if (uniqueReq !== updatedValues.uniqueRequirements) {
      unsavedChanges = "Unique requirements has been changed but not updated";
    } else if (notes !== updatedValues.notes) {
      unsavedChanges = "Notes has been changed but not updated";
    } else {
      props.onClose();
    }
    setUnsavedPopup(unsavedChanges);
  };

  return (
    <>
      <Popup
        show={unsavedPopup.length ? true : false}
        title="Are you sure you want to close?"
        onClose={() => setUnsavedPopup("")}
        primaryCtaOnClick={() => setUnsavedPopup("")}
        primaryCtaText="Continue to Update"
        secondaryCtaText="Close"
        secondaryCtaOnClick={props.onClose}
      >
        {unsavedPopup}
      </Popup>
      {selectedUnderwriter !== null || addUnderwriter ? (
        removeUnderwriter !== 0 ? (
          <div className="underwriter-remove-confirmation">
            <Popup
              show={removeUnderwriter !== 0 ? true : false}
              onClose={() => setRemoveUnderwriter(0)}
              title="Remove Underwriter"
              size="lg"
              primaryCtaText="Remove"
              primaryCtaOnClick={removeUnderwriterApi}
              secondaryCtaText="Close"
              secondaryCtaOnClick={() => setRemoveUnderwriter(0)}
            >
              <span>
                Are you sure you want to remove this underwriter? This action
                cannot be undone
              </span>
            </Popup>
          </div>
        ) : (
          <div className="add-edit-underwriter-container">
            <span className="underwriter-close-btn">
              <CloseIcon
                className="edit-icon underwriter-close-icon"
                onClick={closeunderwriterDialog}
              />
            </span>
            <div className="underwriter-header-container">
              <div className="underwriter-heading">
                {addUnderwriter ? "Add" : "Edit"} underwriter
              </div>
              <div className="underwriter-controls">
                <div className="underwriter-action-btn">
                  <div>
                    <Button
                      buttonText="Back"
                      onClick={closeunderwriterDialog}
                      type="primary-outline"
                      customClassName="underwriter-primary-btn"
                    />
                  </div>
                  {!addUnderwriter ? (
                    <div>
                      <Button
                        buttonText="Remove underwriter"
                        onClick={handleRemoveUnderwriter}
                        type="secondary-outline"
                        customClassName="underwriter-remove-btn"
                      />
                    </div>
                  ) : null}
                  <div>
                    <Button
                      buttonText={addUnderwriter ? "Add" : "Update"}
                      onClick={handleUpdateUnderwriter}
                      type="primary-outline"
                      customClassName="underwriter-primary-btn"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="add-edit-underwriter-content">
              {underwriterFields.map((field, index) => {
                return (
                  <div
                    key={`ur-${field.id}-${index}`}
                    className={classNames(
                      "underwriter-field",
                      field.width === "200%" ? "full-width" : ""
                    )}
                  >
                    <div className="underwriter-field-title">{field.title}</div>
                    {field.id === "name" ? (
                      <div
                        className="contact-list-add"
                        ref={dropdownRef}
                        onFocus={() => setShowContacts(true)}
                      >
                        <TextInput
                          type="text"
                          style={{ width: field.width, ...style }}
                          placeholder={field.placeholder}
                          text={
                            newUnderwriter[
                              field.id as keyof typeof newUnderwriter
                            ]
                          }
                          onChange={(text) => filterContacts(text)}
                        />
                        {showContacts ? (
                          <div className="contact-list-dropdown">
                            {contactsList.map((data: any, index) => {
                              return (
                                <div
                                  key={`contact-add-${data.id}-${index}`}
                                  className="contact-list-item"
                                  onClick={() => selectedContact(data.id)}
                                >
                                  {data.name}
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div>
                        {" "}
                        <TextInput
                          type="text"
                          style={{ width: field.width, ...style }}
                          placeholder={field.placeholder}
                          text={
                            newUnderwriter[
                              field.id as keyof typeof newUnderwriter
                            ]
                          }
                          onChange={(text) =>
                            handleUnderwriterData(field.id, text)
                          }
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )
      ) : (
        <div className="card-detail-view-container">
          <Popup
            title="Delete"
            onClose={() => setShowDelete(false)}
            size="lg"
            show={showDelete}
            primaryCtaText="Delete"
            primaryCtaOnClick={handleDelete}
            secondaryCtaOnClick={() => setShowDelete(false)}
            secondaryCtaText="Cancel"
            customClassName="file-delete-popup"
          >
            Are you sure you want to delete this card <b>{name}</b> with
            coverage - <b>{coverage.value}</b>? This action cannot be undone.
          </Popup>
          <div className="card-detail-view-content">
            <CardHeader
              add={false}
              edit={edit}
              onClose={handleOnClose}
              name={name}
              type={type}
              imgUrl={logo}
              distribution={distribution}
              view="detail"
              onEdit={() => handleEdit()}
              onUpdate={(reqBody, field) => handleHeaderUpdate(reqBody, field)}
              onWholesalerSelect={(selected) => onWholesalersSelect(selected)}
              onGroupIdSelected={(id) => props.onGroupIdSelected(id)}
              groupId={props.data.groupId}
              categories={categories}
              onDelete={() => setShowDelete(true)}
            />
            <div
              className={classNames(
                "last-updated-text",
                wholesalerBg(type, distribution) ? "card-detail-view-bg" : ""
              )}
            >
              Last updated on {lastUpdatedOn}
            </div>
            <div className="card-seperator detail"></div>
            <CardCoverageFacility
              edit={edit}
              view="detail"
              coverage={coverage}
              facilityTypes={facilityTypes}
              onUpdate={(id, value, field, logo) =>
                handleUpdateCoverage(id, value, field, logo)
              }
            />
            <div className="tiv-cms-container">
              <div className="tiv-cms-content">
                <span>$</span>
                <span className="tiv-cms-label-text">
                  Min TIV:
                  {edit ? (
                    <span className="tiv-cms-edit">
                      <TextInput
                        text={minimumTiv}
                        placeholder="Min TIV"
                        onChange={(text) => setMinimumTiv(text)}
                        type="text"
                        className="tiv-cms-tex-edit"
                      />
                      <Edit
                        onUpdate={handleUpdateMinimumTiv}
                        onClose={() => setMinimumTiv(props.data.minimumTiv)}
                      />
                    </span>
                  ) : (
                    minimumTiv
                  )}
                </span>
              </div>
              <div className="tiv-cms-content">
                <CmsIcon width={16} />
                <span className="tiv-cms-label-text">
                  Min CMS:
                  {edit ? (
                    <span className="tiv-cms-edit">
                      <TextInput
                        text={minimumCms}
                        placeholder="Min CMS"
                        onChange={(text) => setMinimumCms(text)}
                        type="text"
                        className="tiv-cms-tex-edit"
                      />
                      <Edit
                        onUpdate={handleUpdateMinimumCms}
                        onClose={() => setMinimumCms(props.data.minimumCms)}
                      />
                    </span>
                  ) : (
                    minimumCms
                  )}
                </span>
              </div>
            </div>
            <div className="standard-req-container">
              <div className="standard-req-items">
                <span className="text-bold">Standard Requirements:</span>
                {filterData.submissionReq.map((req, index) => {
                  return (
                    <span
                      className={classNames(
                        "facility-type standar-req-item",
                        submissionRequirements.includes(req.id.toString())
                          ? "selected"
                          : "not-selected",
                        edit ? "edit-mode" : ""
                      )}
                      key={`sub-req-${index}`}
                      onClick={
                        edit
                          ? () => handleSubmissionRequirements(req.id)
                          : () => null
                      }
                    >
                      {req.value}
                    </span>
                  );
                })}
                {submissionreqId !== 0 ? (
                  <span className="edit-loader" />
                ) : null}
              </div>
            </div>
            <div className="unique-req-container">
              <span className="text-bold">Unique Requirements: </span>
              {edit ? (
                <>
                  <TextInput
                    type="text"
                    placeholder="Unique requirements"
                    onChange={(text) => setUniqueReq(text)}
                    text={uniqueReq}
                    className="unique-req-edit-text"
                  />
                  <Edit
                    onClose={() => setUniqueReq(uniqueRequirements)}
                    onUpdate={handleUniqueReqUpdate}
                  />
                </>
              ) : (
                <span className="text-normal">{uniqueReq}</span>
              )}
            </div>
            <Tabs
              defaultActiveKey="underwriter"
              id="uncontrolled-tab-example"
              className="card-detail-tabs"
            >
              <Tab
                eventKey="underwriter"
                title={
                  loadingUnderwriter ? (
                    <span className="card-detail-tab-loading">
                      <span>Underwriter (</span>
                      <span className="edit-loader" />
                      <span>)</span>
                    </span>
                  ) : (
                    `Underwriter(${underwriterData.length})`
                  )
                }
                className="card-detail-tab"
                style={{
                  border: "none",
                }}
              >
                {getUserPermisson(permissions, "C") ? (
                  <div className="add-underwriter-container">
                    <AddIcon width={12} />
                    <div
                      className="add-underwriter-text"
                      onClick={handleAddNewUnderwriter}
                    >
                      Add underwriter
                    </div>
                  </div>
                ) : null}
                <Underwriter
                  data={underwriterData}
                  selectedCoverageId={coverage.id}
                  onSelectedId={(id) => handleOnSelectUnderwriter(id)}
                />
              </Tab>
              <Tab
                eventKey="states"
                title={
                  loadingStates ? (
                    <span className="card-detail-tab-loading">
                      <span>States covered(</span>
                      <span className="edit-loader" />
                      <span>)</span>
                    </span>
                  ) : (
                    `States covered(${statesData.length})`
                  )
                }
              >
                <States
                  data={statesData}
                  companyDetailId={id}
                  edit={edit}
                  onChange={(id, action, data) =>
                    handleStateChange(id, action, data)
                  }
                />
              </Tab>
              <Tab
                eventKey="files"
                title={
                  loadingFiles ? (
                    <span className="card-detail-tab-loading">
                      <span>Files (</span>
                      <span className="edit-loader" />
                      <span>)</span>
                    </span>
                  ) : (
                    `Files (${files.length})`
                  )
                }
              >
                <Files data={files} groupId={groupId} getFiles={getFiles} />
              </Tab>
            </Tabs>
            <div className="notes-container">
              <div className="notes-title-icon">
                <div className="text-bold">Notes</div>
                {edit ? (
                  <Edit
                    onClose={() => setNotes(props.data.notes)}
                    onUpdate={handleUpdateNotes}
                  />
                ) : null}
              </div>
              {edit ? (
                <TextInput
                  type="textarea"
                  text={notes}
                  placeholder="Notes"
                  className="card-detail-view-text-input"
                  onChange={(text: string) => setNotes(text)}
                  cols={96}
                />
              ) : (
                <span className="text-normal">{notes}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
